import { Button, Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import { map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { merchantGroupsApi, merchantOperatorsApi, shopsApi } from 'api';
import {
  CopyText,
  DataWrapper,
  FormControls,
  FormikCheckbox,
  FormikTextField,
  ListFilter,
  PageHeader,
  SelectionList,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey, UserRole } from 'enums';
import { useMutation, useShopsQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { MerchantGroup, MerchantOperator, Shop } from 'types';
import { formatUtils, validationUtils } from 'utils';

type Values = Pick<MerchantGroup, 'name' | 'payin' | 'payout'>;

export const MerchantGroupDetailsPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation(TranslationNamespace.Merchant, {
    keyPrefix: 'pages.group_details',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const [initialValues, setInitialValues] = useState<Values>({
    name: '',
    payin: true,
    payout: true,
  });

  const [shops, setShops] = useState<Shop[]>([]);
  const [operators, setOperators] = useState<MerchantOperator[]>([]);

  const [selectedShopIds, setSelectedShopIds] = useState<string[]>([]);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);

  const [sortShopIds, setSortShopIds] = useState<string[]>([]);
  const [sortOperatorIds, setSortOperatorIds] = useState<string[]>([]);

  const isNew = useMemo(() => id === NEW_ID, [id]);
  const title = useMemo(
    () => (isNew ? t('title_create') : t('title_edit')),
    [t, isNew],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().trim().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const shopLabelGetter = useCallback(
    (shop: Shop) => formatUtils.formatName(shop),
    [],
  );
  const operatorLabelGetter = useCallback(
    (operator: MerchantOperator) =>
      operator?.user?.name || operator?.user?.email || '',
    [],
  );

  const setInitialValuesFromGroup = useCallback((group: MerchantGroup) => {
    setInitialValues({
      name: group.name,
      payin: group.payin,
      payout: group.payout,
    });
    const shopIds = map(group.shops, (shop) => shop.id || '');
    const operatorIds = map(group.operators, (operator) => operator.id || '');
    setSelectedShopIds(shopIds);
    setSelectedOperatorIds(operatorIds);
    setSortShopIds(shopIds);
    setSortOperatorIds(operatorIds);
  }, []);

  const queryResultGroup = useQuery(
    [QueryKey.MerchantGroups, id],
    () => merchantGroupsApi.getOne(id),
    {
      enabled: !isNew,
      onSuccess: setInitialValuesFromGroup,
    },
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(UserRole.Merchant),
    {
      onSuccess: setShops,
    },
  );

  const queryResultOperators = useQuery(
    QueryKey.MerchantOperators,
    merchantOperatorsApi.getAll,
    {
      onSuccess: setOperators,
    },
  );

  const queryResults = useMemo(
    () => [queryResultGroup, queryResultShops, queryResultOperators],
    [queryResultGroup, queryResultShops, queryResultOperators],
  );

  const { mutate: createGroup } = useMutation(merchantGroupsApi.create);
  const { mutate: updateGroup } = useMutation(merchantGroupsApi.update);

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const options = {
        onSuccess: () => {
          navigate(ROUTE_PATH.MERCHANT.GROUPS);
        },
        onError: (error: AxiosError) => {
          formikHelpers.setErrors(validationUtils.getFormErrors(error));
        },
        onSettled: () => formikHelpers.setSubmitting(false),
      };

      const data = {
        ...values,
        shopIds: selectedShopIds,
        operatorIds: selectedOperatorIds,
      };

      if (isNew) {
        createGroup(data, options);
      } else {
        updateGroup({ id, data }, options);
      }
    },
    [
      id,
      isNew,
      navigate,
      createGroup,
      updateGroup,
      selectedShopIds,
      selectedOperatorIds,
    ],
  );

  return (
    <Fragment>
      <PageHeader title={title} />
      <DataWrapper queryResult={queryResults} ignoreState={{ empty: isNew }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form>
              <div className="tw-mb-6">
                <div className="tw-text-xl tw-mb-4">{t('general_info')}</div>
                <FormControls>
                  {!isNew && (
                    <CopyText
                      className="tw-mb-4"
                      text={queryResultGroup.data?.id || ''}
                    />
                  )}
                  <FormikTextField
                    label={t('fields.name')}
                    name="name"
                    type="text"
                    fullWidth
                    required
                    sx={{ maxWidth: 400 }}
                  />
                  <div>
                    <FormikCheckbox name="payin" label={t('fields.payin')} />
                    <FormikCheckbox name="payout" label={t('fields.payout')} />
                  </div>
                </FormControls>
              </div>
              <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                  <ListFilter items={shops} labelGetter={shopLabelGetter}>
                    {(items, renderInput) => (
                      <SelectionList
                        title={t('fields.shops')}
                        items={items}
                        selectedIds={selectedShopIds}
                        sortByIds={sortShopIds}
                        onSelectedIdsChange={setSelectedShopIds}
                        renderInput={renderInput}
                        labelGetter={shopLabelGetter}
                        noSelectionText={t('no_selection')}
                      />
                    )}
                  </ListFilter>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                  <ListFilter
                    items={operators}
                    labelGetter={operatorLabelGetter}
                  >
                    {(items, renderInput) => (
                      <SelectionList
                        title={t('fields.operators')}
                        items={items}
                        selectedIds={selectedOperatorIds}
                        sortByIds={sortOperatorIds}
                        onSelectedIdsChange={setSelectedOperatorIds}
                        renderInput={renderInput}
                        labelGetter={operatorLabelGetter}
                        noSelectionText={t('no_selection')}
                      />
                    )}
                  </ListFilter>
                </Grid>
              </Grid>
              <div className="tw-mt-8">
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={formik.isSubmitting}
                  onClick={formik.submitForm}
                >
                  {tCommon('buttons.save')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DataWrapper>
    </Fragment>
  );
};
